import React, { useState } from 'react'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { MarginType } from 'common/types/styleTypes'
import { OptionWrapper } from 'src/editor/layout/sidebar/settings/option-wrapper'
import { ExpandButton } from 'src/editor/shared/ui-lib/expand-button'
import { MobileIcon } from 'src/editor/shared/ui-lib/mobile-icon'
import { MarginBottomIcon } from './icons/margin-bottom-icon'
import { MarginTopIcon } from './icons/margin-top-icon'
import { MarginLeftIcon } from './icons/margin-left-icon'
import { MarginRightIcon } from './icons/margin-right-icon'
import { MarginLeftRightIcon } from './icons/margin-left-right-icon'
import { MarginTopBottomIcon } from './icons/margin-top-bottom-icon'
import { NumberInput } from 'src/editor/shared/ui-lib/input/number-input'

const INPUT_MIN_VALUE = -1000

interface MarginStyleProps {
  margin: Partial<MarginType> | MarginType | undefined
  update: (margin: Partial<MarginType>) => void
  label?: string
  allowNegative?: boolean
}

type SideType =
  | 'all'
  | 'top-bottom'
  | 'left-right'
  | 'marginLeft'
  | 'marginRight'
  | 'marginTop'
  | 'marginBottom'

export const MarginGroup = ({
  margin = { marginTop: 0, marginRight: 0, marginBottom: 0, marginLeft: 0 },
  update,
  label = 'settings_styles.margin.label',
}: MarginStyleProps) => {
  const { isMobile } = useDeviceModeContext()
  const getIsVisibleFullSettings = (): boolean => {
    return !(
      margin.marginTop === margin.marginBottom &&
      margin.marginLeft === margin.marginRight
    )
  }
  const [isVisibleFullSetting, setIsVisibleFullSetting] = useState(
    getIsVisibleFullSettings,
  )

  const switchSettings = () => {
    if (isVisibleFullSetting) {
      handleUpdate()
    }
    setIsVisibleFullSetting(!isVisibleFullSetting)
  }

  const handleUpdate =
    (type: SideType = 'all') =>
    (value = 0) => {
      switch (type) {
        case 'top-bottom':
          return update({
            ...margin,
            marginTop: +value,
            marginBottom: +value,
          })
        case 'left-right':
          return update({
            ...margin,
            marginLeft: +value,
            marginRight: +value,
          })
        case 'marginBottom':
        case 'marginLeft':
        case 'marginRight':
        case 'marginTop':
          return update({
            ...margin,
            [type]: +value,
          })
        default:
          return update({
            marginTop: +(margin.marginTop ?? margin.marginBottom ?? 0),
            marginBottom: +(margin.marginTop ?? margin.marginBottom ?? 0),

            marginRight: +(margin.marginLeft ?? margin.marginRight ?? 0),
            marginLeft: +(margin.marginLeft ?? margin.marginRight ?? 0),
          })
      }
    }

  return (
    <>
      <OptionWrapper
        labelText={label}
        labelIcon={isMobile ? <MobileIcon /> : null}
      >
        <div className="flex gap-[6px]">
          <div className="grid grow-1 grid-cols-2 gap-3">
            {!isVisibleFullSetting ? (
              <>
                <NumberInput
                  startElement={<MarginTopBottomIcon />}
                  onChange={handleUpdate('top-bottom')}
                  value={margin.marginTop ?? margin.marginBottom ?? 0}
                  min={INPUT_MIN_VALUE}
                />
                <NumberInput
                  startElement={<MarginLeftRightIcon />}
                  onChange={handleUpdate('left-right')}
                  value={margin.marginLeft ?? margin.marginRight ?? 0}
                  min={INPUT_MIN_VALUE}
                />
              </>
            ) : (
              <>
                <NumberInput
                  startElement={<MarginTopIcon />}
                  onChange={handleUpdate('marginTop')}
                  value={margin.marginTop ?? 0}
                  min={INPUT_MIN_VALUE}
                />
                <NumberInput
                  startElement={<MarginLeftIcon />}
                  onChange={handleUpdate('marginLeft')}
                  value={margin.marginLeft ?? 0}
                  min={INPUT_MIN_VALUE}
                />
                <NumberInput
                  startElement={<MarginBottomIcon />}
                  onChange={handleUpdate('marginBottom')}
                  value={margin.marginBottom ?? 0}
                  min={INPUT_MIN_VALUE}
                />
                <NumberInput
                  startElement={<MarginRightIcon />}
                  onChange={handleUpdate('marginRight')}
                  value={margin.marginRight ?? 0}
                  min={INPUT_MIN_VALUE}
                />
              </>
            )}
          </div>
          <ExpandButton
            className="relative top-[5.5px]"
            isActive={isVisibleFullSetting}
            onClick={switchSettings}
          />
        </div>
      </OptionWrapper>
    </>
  )
}
